import { watchPreviewRoot } from './editor-preview-root.js';

// Pauses the Recommended-card glow ring's animation (breathing or spinning
// rays, whichever the block's "Recommended Card Highlight" field picked --
// see assets/src/scss/components/_event-card-recommended-glow.scss's own
// doc comment) while its card is scrolled out of the viewport, and resumes
// it on scroll back in -- the same rationale as
// modules/swiper-viewport-autoplay.js, applied to a theme animation
// instead of a third-party plugin's. .event-card__glow-ring-anim is the
// shared class both modes render (--breathe or --rays), so this manages
// either one identically without needing to know which is active; 'static'
// mode renders no such element at all, so this module simply finds nothing
// to manage for those cards.
//
// IntersectionObserver's callback isn't fully reliable across a
// scroll-behavior:smooth jump past this element (see
// modules/swiper-viewport-autoplay.js's doc comment for the confirmed
// failure mode: an "entered" transition can fire mid-scroll with the
// matching "left again" transition never following once the scroll
// settles). A periodic getBoundingClientRect() sweep is the source of
// truth and self-corrects anything the observer's callback missed; the
// observer stays alongside it purely so an ordinary scroll still reacts
// immediately instead of waiting for the next sweep.
//
// A per-instance count cap was tried and measured out as unnecessary: with
// the same DOM held constant, toggling several real cards' anims between
// all-running and all-paused moved total main-thread cost by only ~1.14x
// (~40ms/s) -- once genuinely idle/paused off-screen, essentially free
// either way. The technique itself isn't the expensive part; it doesn't
// need a scaling safeguard.
//
// START_DELAY_MS: the moment a card first scrolls into view, the browser
// already has real, unavoidable raster work queued for it -- decoding its
// (loading="lazy") image and rasterizing the card's own newly-visible
// content for the first time. Confirmed via a CDP trace with raster/GPU
// categories enabled: that work funnels through a single
// RasterDecoderImpl::DoEndRasterCHROMIUM / CommandBuffer::Flush chain, and
// starting the ring's rotation in that exact same frame adds one more
// layer (its own mask + gradient) competing for the same GPU raster pass.
// Repeated real-wheel-scroll trials (3x with the ring, 3x without, same
// page) showed this reliably costs ~1.8x more main-thread work during the
// transition and occasionally tips a frame over the 16ms/60fps budget
// (up to 24.7ms, 3 over-budget frames in one trial) -- with the ring
// disabled, zero over-budget frames across all trials. A short delay
// before the very first start moves the ring's own raster pass to a
// separate, otherwise-idle frame instead of stacking it onto the image's,
// at zero visible cost (the ring reads as decorative chrome around
// already-visible content, not something a visitor is waiting on).
const CORRECTION_INTERVAL_MS = 1500;
const VIEWPORT_MARGIN_PX = 200;
const START_DELAY_MS = 250;

export default function initRecommendedGlowViewport() {
    if (typeof window.IntersectionObserver !== 'function') {
        return;
    }

    // Two contexts share this markup/class (see
    // assets/src/scss/components/_event-card-recommended-glow.scss's own doc
    // comment for why -- Recommended Events deliberately has no glow ring at
    // all, product decision) -- both need managing here identically.
    const SELECTOR = [
        '.events-grid .event-card--recommended .event-card__glow-ring-anim',
        '.all-events-block .event-card--recommended .event-card__glow-ring-anim',
    ].join(', ');

    const isNearViewport = (el) => {
        const rect = el.getBoundingClientRect();
        // A display:none card (events-ajax.js's "load more" pagination
        // hides everything past the initial page with display:none) reports
        // an all-zero rect -- (0,0,0,0) numerically satisfies "near the top
        // of the viewport" below, which would wrongly keep its ring
        // running. A zero width/height is never a real on-screen size, so
        // treat it as definitely not visible instead.
        if (rect.width === 0 && rect.height === 0) {
            return false;
        }
        return (
            rect.top < window.innerHeight + VIEWPORT_MARGIN_PX && rect.bottom > -VIEWPORT_MARGIN_PX
        );
    };

    const pendingStarts = new WeakMap();

    const correct = (el) => {
        if (isNearViewport(el)) {
            if (el.style.animationPlayState === 'running' || pendingStarts.has(el)) {
                return;
            }
            const timer = window.setTimeout(() => {
                pendingStarts.delete(el);
                el.style.animationPlayState = 'running';
            }, START_DELAY_MS);
            pendingStarts.set(el, timer);
        } else {
            const timer = pendingStarts.get(el);
            if (timer) {
                window.clearTimeout(timer);
                pendingStarts.delete(el);
            }
            el.style.animationPlayState = 'paused';
        }
    };

    const observer = new IntersectionObserver(
        (entries) =>
            entries.forEach((entry) => {
                correct(entry.target);
            }),
        { rootMargin: `${VIEWPORT_MARGIN_PX}px 0px` }
    );

    // ACF's ServerSideRender (Upcoming Events block preview in the block
    // editor) inserts its real markup -- these ring elements included --
    // asynchronously, after $(document).ready() has already run this
    // module's init once and found nothing to observe. A DOM-mutation
    // rescan (the same pattern recommended-glow-canvas.js's own doc comment
    // explains in full, and that initEventsPreviewScale()/
    // initRecommendedEventsReorder() already use) picks up each later batch
    // as it actually appears instead of silently never animating anything
    // for the rest of that editing session. attached tracks which elements
    // are already observed so a rescan doesn't re-register the same node.
    //
    // The block editor's own preview markup (these ring elements included)
    // lives inside Gutenberg's iframed editor canvas, a separate document
    // from the one this script loads into -- watchPreviewRoot() (see its own
    // doc comment) resolves the right document to scan/observe instead of
    // always scanning the outer `document`, where these rings never exist
    // while previewing inside the editor. Confirmed live as the reason the
    // glow never animated in ANY block editor before this, front end
    // unaffected.
    const attached = new WeakSet();
    const anims = [];

    const attachAll = (root) => {
        // Prunes entries for elements a since-replaced ACF re-render already
        // removed from the DOM -- see recommended-glow-canvas.js's own
        // (identical-shape) doc comment for why this is a real, unbounded
        // leak, not just untidy state: IntersectionObserver keeps a strong
        // reference to every target it's observing for as long as that
        // observation is active, so without unobserve() here a detached
        // ring element stays reachable forever, and the correction interval
        // below keeps iterating all of them on top of that.
        for (let i = anims.length - 1; i >= 0; i--) {
            const el = anims[i];
            if (el.isConnected) {
                continue;
            }
            anims.splice(i, 1);
            observer.unobserve(el);
            const timer = pendingStarts.get(el);
            if (timer) {
                window.clearTimeout(timer);
                pendingStarts.delete(el);
            }
        }
        root.querySelectorAll(SELECTOR).forEach((el) => {
            if (attached.has(el)) {
                return;
            }
            attached.add(el);
            anims.push(el);
            observer.observe(el);
            correct(el);
        });
    };

    watchPreviewRoot((root) => {
        attachAll(root);
        new MutationObserver(() => attachAll(root)).observe(root.body, {
            childList: true,
            subtree: true,
        });
    });

    window.setInterval(() => anims.forEach(correct), CORRECTION_INTERVAL_MS);
}
